import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Compass, Heart, Wallet, LogIn } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppDock from "@/components/WhatsAppDock";
import { useCustomer } from "@/lib/auth";
import MyTrips from "@/components/account/MyTrips";
import SavedWishlist from "@/components/account/SavedWishlist";
import ReferEarn from "@/components/account/ReferEarn";

const TABS = [
  { key: "trips", label: "My trips", icon: Compass },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "earn", label: "Refer & earn", icon: Wallet },
];

export default function Account() {
  const { user, loading, login } = useCustomer();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "trips";
  const [ready, setReady] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); setReady(true); }, []);

  return (
    <div className="min-h-screen bg-sand text-ink">
      <Nav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24" data-testid="account-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-ink/50 hover:text-ink" data-testid="account-back-home"><ArrowLeft size={13} /> Home</Link>
        {loading || !ready ? (
          <p className="mt-10 text-sm text-ink/50" data-testid="account-loading">Loading your account…</p>
        ) : !user ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 max-w-xl" data-testid="account-signin-prompt">
            <h1 className="font-serif text-4xl sm:text-5xl font-bold">Your Puri account</h1>
            <p className="mt-4 text-ink/70 leading-relaxed">Sign in with Google to see your bookings and confirmation cards, keep your wishlist across devices, and earn 10% on every trip booked through your referral link.</p>
            <button onClick={() => login("/account")} data-testid="account-signin-button" className="mt-8 inline-flex items-center gap-2 bg-ink text-sand font-semibold px-6 py-3.5 rounded-full hover:bg-terra transition-colors"><LogIn size={16} /> Sign in with Google</button>
          </motion.div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-end justify-between gap-6 min-w-0">
              <div className="flex items-center gap-4 min-w-0">
                {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-16 h-16 rounded-full ring-4 ring-white" data-testid="account-avatar" /> : <span className="w-16 h-16 rounded-full bg-ink text-sand grid place-items-center text-2xl font-bold">{user.name?.[0]}</span>}
                <div className="min-w-0">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-terra">Welcome back</p>
                  <h1 className="font-serif text-3xl sm:text-4xl font-bold leading-tight break-words" data-testid="account-name">{user.name}</h1>
                  <p className="text-sm text-ink/50 truncate" data-testid="account-email">{user.email}</p>
                </div>
              </div>
              <div className="flex gap-2 bg-white border border-ink/10 rounded-full p-1" data-testid="account-tabs">
                {TABS.map((t) => (
                  <button key={t.key} onClick={() => setParams({ tab: t.key })} data-testid={`account-tab-${t.key}`}
                    className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${tab === t.key ? "bg-ink text-sand" : "text-ink/60 hover:text-ink"}`}>
                    <t.icon size={14} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-10">
              {tab === "trips" && <MyTrips />}
              {tab === "wishlist" && <SavedWishlist />}
              {tab === "earn" && <ReferEarn />}
            </div>
          </>
        )}
      </main>
      <Footer />
      <WhatsAppDock />
    </div>
  );
}
